import { css } from '@bts-web/utils-style-engine';
import { ButtonComponentProps } from '../../types';
import { FilledSpinnerIos } from '../../svgIcons';

export type ButtonSizeVariants = 'extra_small' | 'small' | 'medium' | 'large';

export type ButtonProps = ButtonComponentProps & { className?: string };

export type ButtonVisualVariants =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'ghostInverted'
  | 'ghostOutlined'
  | 'accent'
  | 'primaryOnBrand';

export const ButtonBase: (
  props: ButtonComponentProps,
) => React.ReactElement = ({
  size = 'medium',
  visual = 'accent',
  children,
  withBorderRadius = true,
  noPadding = false,
  fullWidth = false,
  // TODO: Disable hover styles temporarily due to visual issues. Investigate and restore hover behavior.
  noHover = true,
  tabIndex = 0,
  isLoading,
  className,
  ...rest
}) => {
  return (
    <button
      data-testid="button"
      tabIndex={tabIndex}
      type="button"
      className={`${css({
        '&:has(a:only-child)': {
          px: 0,
          py: 0,
          '& > a': {
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
        border: '1px solid',
        borderColor: 'transparent',
        display: fullWidth ? 'flex' : 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        gap: 'extra_small_2',
        ...(withBorderRadius
          ? {
              borderEndEndRadius: 'buttonBottomRight',
              borderEndStartRadius: 'buttonTopRight',
              borderStartStartRadius: 'buttonTopLeft',
              borderStartEndRadius: 'buttonBottomLeft',
            }
          : {}),
        cursor: !rest.disabled ? 'pointer' : 'default',
        maxWidth: {
          base: !fullWidth ? 342 : '100%',
        },
        width: fullWidth ? '100%' : 'auto',
        _disabled: {
          pointerEvents: 'none',
          cursor: 'not-allowed',
        },
        '& > [data-element="loading-icon"]': {
          animation: 'spin 1s linear infinite',
        },
        ...(visual === 'primary'
          ? {
              backgroundColor: 'brand.fill_primary',
              color: 'neutrals.text_primary_inverted',
              _active: {
                backgroundColor: 'brand.fill_pressed',
                color: 'neutrals.text_primary_inverted',
              },
              _focus: {
                backgroundColor: 'brand.fill_pressed',
                color: 'neutrals.text_primary_inverted',
              },
              _disabled: {
                backgroundColor: 'neutrals.fill_disabled',
                color: 'neutrals.text_disabled',
                _hover: {
                  backgroundColor: 'neutrals.fill_disabled',
                  color: 'neutrals.text_disabled',
                },
              },
            }
          : {}),
        ...(visual === 'tertiary'
          ? {
              borderColor: 'transparent',
              backgroundColor: 'neutrals.fill_quinary',
              color: 'neutrals.text_primary',
              _active: {
                backgroundColor: 'neutrals.fill_quaternary',
                color: 'neutrals.text_primary',
              },
              _focus: {
                backgroundColor: 'neutrals.fill_focused',
                color: 'neutrals.text_primary',
              },
              _disabled: {
                backgroundColor: 'neutrals.fill_disabled',
                color: 'neutrals.text_disabled',
                _hover: {
                  backgroundColor: 'neutrals.fill_disabled',
                  color: 'neutrals.text_disabled',
                },
              },
            }
          : {}),
        ...(visual === 'secondary'
          ? {
              borderColor: 'neutrals.stroke_primary',
              backgroundColor: 'neutrals.card_fill_primary',
              color: 'neutrals.text_primary',
              _active: {
                backgroundColor: 'neutrals.card_fill_primary',
                color: 'neutrals.text_primary',
                borderColor: 'neutrals.stroke_primary',
              },
              _focus: {
                backgroundColor: 'neutrals.fill_focused',
                color: 'neutrals.text_primary',
                borderColor: 'neutrals.stroke_primary',
              },
              _disabled: {
                backgroundColor: 'neutrals.card_fill_primary',
                color: 'neutrals.text_disabled',
                borderColor: 'neutrals.stroke_primary_disabled',
                _hover: {
                  backgroundColor: 'neutrals.card_fill_primary',
                  color: 'neutrals.text_disabled',
                  borderColor: 'neutrals.stroke_primary_disabled',
                },
              },
            }
          : {}),
        ...(visual === 'ghost'
          ? {
              color: 'neutrals.text_primary',
              _active: {
                color: 'neutrals.text_primary',
              },
              _focus: {
                color: 'neutrals.text_primary',
              },
              _disabled: {
                color: 'neutrals.text_disabled',
                _hover: {
                  color: 'neutrals.text_disabled',
                },
              },
            }
          : {}),
        ...(visual === 'ghostInverted'
          ? {
              color: 'neutrals.text_primary_inverted',
              _active: {
                color: 'neutrals.text_primary',
              },
              _focus: {
                color: 'neutrals.text_primary_inverted',
              },
            }
          : {}),
        ...(visual === 'ghostOutlined'
          ? {
              borderColor: 'neutrals.stroke_primary',
              backgroundColor: 'transparent',
              color: 'neutrals.text_primary',
              _hover: {
                backgroundColor: 'neutrals.fill_quinary',
                color: 'neutrals.text_primary',
              },
              _active: {
                backgroundColor: 'neutrals.fill_quaternary',
                color: 'neutrals.text_primary',
                borderColor: 'neutrals.stroke_pressed',
              },
              _focus: {
                backgroundColor: 'neutrals.fill_focused',
                color: 'neutrals.text_primary',
                borderColor: 'neutrals.stroke_focused',
              },
              _disabled: {
                backgroundColor: 'transparent',
                color: 'neutrals.text_disabled',
                borderColor: 'neutrals.stroke_primary_disabled',
                _hover: {
                  backgroundColor: 'transparent',
                  color: 'neutrals.text_disabled',
                  borderColor: 'neutrals.stroke_primary_disabled',
                },
              },
            }
          : {}),
        ...(visual === 'accent'
          ? {
              borderColor: 'brand.fill_primary',
              backgroundColor: 'brand.fill_primary',
              color: 'brand.on_fill_primary',
              ...(!noHover
                ? {
                    _hover: {
                      backgroundColor: 'brand.fill_focused',
                      color: 'brand.on_fill_primary',
                    },
                  }
                : {}),
              _active: {
                backgroundColor: 'brand.fill_pressed',
                color: 'brand.on_fill_primary',
              },
              _focus: {
                backgroundColor: 'brand.fill_pressed',
                color: 'brand.on_fill_primary',
              },
              _disabled: {
                borderColor: 'brand.fill_disabled',
                backgroundColor: 'neutrals.fill_disabled',
                color: 'neutrals.text_disabled',
                _hover: {
                  backgroundColor: 'neutrals.fill_disabled',
                  color: 'neutrals.text_disabled',
                },
              },
            }
          : {}),
        ...(visual === 'primaryOnBrand'
          ? {
              backgroundColor: 'neutrals.on_fill_dark',
              color: 'neutrals.on_fill_light',
              _active: {
                backgroundColor: 'neutrals.on_fill_dark',
                color: 'neutrals.on_fill_light',
              },
              _focus: {
                backgroundColor: 'neutrals.fill_focused',
                color: 'neutrals.on_fill_light',
              },
              _disabled: {
                backgroundColor: 'neutrals.on_fill_dark_disabled',
                color: 'neutrals.text_disabled',
              },
            }
          : {}),
        ...(size === 'extra_small'
          ? {
              fontSize: 'body.medium_medium',
              py: 'extra_small_3',
              px: 'extra_small',
              minHeight: 28,
            }
          : {}),
        ...(size === 'small'
          ? {
              fontSize: 'caption.medium_medium',
              py: 'extra_small_2',
              px: 'small',
              minHeight: 38,
            }
          : {}),
        ...(size === 'medium'
          ? {
              fontSize: 'label.medium_medium',
              lineHeight: 'label.medium_medium',
              letterSpacing: 'label.medium_medium',
              fontWeight: 'label.medium_medium',
              padding: 'extra_small_2',
              minHeight: 46,
              width: '100%',
            }
          : {}),
        ...(size === 'large'
          ? {
              fontSize: 'label.large',
              lineHeight: 'label.large',
              letterSpacing: 'label.large',
              fontWeight: 'label.large',
              px: 'extra_small_2',
              py: 18,
              minHeight: 56,
              width: '100%',
            }
          : {}),
        ...(noPadding === true
          ? {
              px: 0,
              py: 0,
              p: 0,
              minHeight: 0,
            }
          : {}),
      })} ${className ?? ''}`}
      {...rest}
    >
      {children}
      {isLoading && (
        <FilledSpinnerIos
          aria-label="loading icon"
          data-element="loading-icon"
          theme="filled"
        />
      )}
    </button>
  );
};
